

import { defineComponent, computed } from 'vue';
import { useStore } from 'vuex';
import { GATEWAY_FOLDER } from '@/functions';
import Loading from '@/components/Loading.vue';
import Select from '@/components/controls/Select.vue';
import Input from '@/components/controls/Input.vue';
import HotelList from '@/components/HotelList.vue';
import HotelStars from '@/components/HotelStars.vue';
import Stats from '@/components/Stats.vue';

export default defineComponent({
  name: 'Hero',
  components: {
    Select,
    Input,
    Loading,
    HotelList,
    HotelStars,
    Stats,
  },
  setup() {
    const $store = useStore();
    const state = computed(() => $store.state);
    const { context } = $store.getters;
    const cells = computed(() => $store.state.cells);
    const initialsList = computed(() => $store.state.initials.list);
    const regionList = computed(() => $store.state.regionList);
    const subRegionList = computed(() => $store.state.subRegionList);
    const isInitialized = computed(() => $store.state.isInitialized);
    const isSaved = computed(() => $store.state.isSaved);

    const {
      isError, isConfig, isLoading, cellResult,
      cellHotel, regionById, subRegionById, hotelInfo,
    } = $store.getters;

    const nextCellHotel = (index: number) => {
      const e = window.event as Event;
      $store.dispatch('sendStats', {
        cell: initialsList.value[index],
        type: (e.target as HTMLDivElement)?.dataset?.stats,
      });

      const isMaxLength = cellResult(index).hotel.length - 1 === cells.value[index].hotelIndex;
      $store.commit('setCellHotel', {
        index,
        hotelIndex: isMaxLength ? 0 : cells.value[index].hotelIndex + 1,
      });

      const { hotelcode } = cellHotel(index);
      if (!hotelInfo(hotelcode)) {
        $store.dispatch('getHotel', hotelcode);
      }
    };
    const setCellText = (e: Event, i: number) => {
      const { value } = e?.target as HTMLInputElement;
      $store.commit('setCell', {
        param: 'text',
        index: i,
        value,
      });
    };

    const setCellRegion = (e: Event, i: number) => {
      const region: string | undefined = (e?.target as HTMLDivElement).dataset.id;
      $store.commit('setCell', {
        param: 'regions',
        index: i,
        value: region,
      });
      $store.commit('setCell', {
        param: 'subregions',
        index: i,
        value: '',
      });
      if (
        subRegionList.value
          .filter((sub: {parentregion: string}) => sub.parentregion === region)
          .length === 0
      ) {
        $store.dispatch('getCellText', i);
      }
    };

    const setCellSubRegion = (e: Event, i: number) => {
      const subregion: string | undefined = (e?.target as HTMLDivElement).dataset.id;
      $store.commit('setCell', {
        param: 'subregions',
        index: i,
        value: subregion,
      });
      $store.dispatch('getCellText', i);
    };

    // datepicker
    const datefrom = computed(() => new Date($store.state.initials.common.datefrom));
    const dateString = computed(() => `${new Date($store.state.initials.common.datefrom).toLocaleDateString('ru-RU')} - ${new Date($store.state.initials.common.dateto).toLocaleDateString('ru-RU')}`);
    const handleDateSelect = ({ date }: { date: Date }) => {
      const df = new Date(date.setHours(12));
      $store.commit('setCommon', { param: 'datefrom', value: df });
    };
    const handleSecondDateSelect = ({ date }: { date: Date }) => {
      const df = new Date(new Date($store.state.initials.common.datefrom).setHours(12));
      const dt = new Date(date.setHours(12));
      if (df.getTime() < dt.getTime()) {
        $store.commit('setCommon', { param: 'dateto', value: dt });
      } else {
        $store.commit('setCommon', { param: 'datefrom', value: dt });
        $store.commit('setCommon', { param: 'dateto', value: df });
      }
    };

    // dates through days
    const dateFromInDays = computed(() => $store.state.options?.datefromindays);
    const dateToInDays = computed(() => $store.state.options?.datetoindays);
    const handleDateFromInDaysSelect = (e: Event) => {
      const { value } = e?.target as HTMLInputElement;
      $store.commit('setDateFromInDays', value);
      console.log(value);
    };
    const handleDateToInDaysSelect = (e: Event) => {
      const { value } = e?.target as HTMLInputElement;
      $store.commit('setDateToInDays', value);
      console.log(value);
    };

    const handleConfigSave = () => $store.dispatch('saveConfig');
    const handleReload = () => window.location.reload();
    const toOrigin = () => {
      window.location.href = `${window.location.origin}${window.location.pathname}`;
      return false;
    };

    const getImage = (hotelcode: string, i: number) => {
      const cardImages = (window as {WTV_CARD_IMAGES?: string[]}).WTV_CARD_IMAGES;
      if (cardImages) {
        return cardImages[i];
      }
      const imageList = hotelInfo(hotelcode)?.images?.image;
      if (!imageList || imageList.length === 0) return `${GATEWAY_FOLDER}assets/loading.webp`;

      return imageList[0];
    };
    const setHotelListVisible = (index: number) => {
      const e = window.event as Event;
      $store.dispatch('sendStats', {
        cell: initialsList.value[index],
        type: (e.target as HTMLDivElement)?.dataset?.stats,
      });
      const isHotelListVisible = !state.value.cells[index].isHotelListVisible;
      $store.commit('setCellHotel', { index, isHotelListVisible });
    };

    const getTour = (hotel: { tours: { tour: unknown[] }} | false) => {
      if (!hotel || !hotel.tours || !hotel.tours.tour) return false;

      return hotel.tours.tour[0];
    };

    const openTour = (i: number) => {
      const e = window.event as Event;
      $store.dispatch('sendStats', {
        cell: initialsList.value[i],
        type: (e.target as HTMLDivElement)?.dataset?.stats,
      });

      const hotel = cellHotel(i);
      const { hotelcode } = hotel;
      const tour = getTour(hotel);
      const list = cellResult(i)?.hotel;
      if (!hotelInfo(hotelcode)) {
        $store.dispatch('getHotel', hotelcode);
      }
      $store.dispatch('actualize', tour);
      $store.commit('setRelatedList', list);
      $store.commit('setCellIndex', i);
    };

    const toggleMeta = (index: number) => {
      const e = window.event as Event;
      const type = (e.target as HTMLDivElement)?.dataset?.stats;
      if (type) {
        $store.dispatch('sendStats', {
          cell: initialsList.value[index],
          type,
        });
      }

      const isMetaOpen = !state.value.cells[index].isMetaOpen;
      $store.commit('setCellHotel', { index, isMetaOpen });
    };
    const getMeta = (index: number) => {
      const tour = getTour(cellHotel(index)) as {
        flydate: string;
        adults: string;
        nights: string;
        mealrussian: string;
        room: string;
      };
      return `
Начало тура: ${tour.flydate},
${tour.adults}&nbsp;взрослых,
${tour.nights}&nbsp;ночей,
${tour.mealrussian},
${tour.room}`;
    };
    const crElem = (
      type: string,
      parent: HTMLElement,
      innerHTML = '',
      attrObj: {[key: string]: string} = {},
    ) => {
      const elt = document.createElement(type);
      if (Object.keys(attrObj).length !== 0) {
        Object.keys(attrObj).forEach((attr) => elt.setAttribute(attr, attrObj[attr]));
        // for (const attr in attrObj) elt.setAttribute(attr, attrObj[attr]);
      }
      elt.innerHTML = innerHTML != null ? innerHTML : '';
      parent.appendChild(elt);
      return elt;
    };

    const handleTextLinkClick = (e = window.event, i: number) => {
      const text = (e?.target as HTMLElement)?.dataset.text;
      if (!text) return;

      $store.dispatch('sendStats', {
        cell: initialsList.value[i],
        type: 'open_text_link',
      });

      (e as Event).stopPropagation();
      (e as Event).preventDefault();

      const container = e?.target as HTMLElement;
      if (container) {
        container.removeAttribute('href');
        if (container.firstElementChild) {
          container.firstElementChild.remove();
        }
        const popup = crElem('div', container, text, { class: 'wtv-link-description' });

        const removePopup = () => {
          (e as Event).stopPropagation();
          (e as Event).preventDefault();
          window.removeEventListener('click', removePopup);
          return (popup as HTMLElement)?.remove();
        };
        window.addEventListener('click', removePopup);
      }
    };

    const removeInitial = (i: number) => $store.commit('removeInitial', i);
    const addInitial = () => $store.commit('addInitial');

    const isCards = computed(() => (window as {WTV_IS_CARDS?: boolean}).WTV_IS_CARDS);

    Object.assign(window, {
      getCacheForHotel: (hotelcode: number) => $store.dispatch('getCache', {
        ...state.value.initials.common,
        hotels: hotelcode,
      }),
    });
    document.dispatchEvent(new Event('getCacheForHotelReady'));
    const discount = computed(() => (window as {WTV_DISCOUNT?: number}).WTV_DISCOUNT || 0);

    cells.value.map((c: unknown) => console.log(c));

    return {
      context,
      state,
      initialsList,
      regionList,
      subRegionList,
      cells,
      isInitialized,
      isError,
      isLoading,
      isConfig,
      isSaved,
      cellResult,
      cellHotel,
      nextCellHotel,
      regionById,
      subRegionById,

      setCellText,
      setCellRegion,
      setCellSubRegion,
      handleConfigSave,
      handleReload,
      toOrigin,

      datefrom,
      dateString,
      handleDateSelect,
      handleSecondDateSelect,

      dateFromInDays,
      dateToInDays,
      handleDateFromInDaysSelect,
      handleDateToInDaysSelect,

      getImage,
      setHotelListVisible,
      toggleMeta,
      getMeta,
      getTour,

      openTour,

      handleTextLinkClick,

      removeInitial,
      addInitial,
      isCards,
      discount,
    };
  },
});
